import { SOCIAL_EXPORT_FRAGMENT_SOURCE } from '@app/modules/fragments/hooks/useAvailableSocialExportSources';
import { webApi } from '@mi-front/api-clients';
import { LocaleRelativeTime } from '@mi-front/ui';
import VideoFileIcon from '@mui/icons-material/VideoFile';
import ViewTimelineOutlinedIcon from '@mui/icons-material/ViewTimelineOutlined';
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { ReactNode } from 'react';
import { useIntl } from 'react-intl';

export interface SocialExportMediaSelectorProps {
  /** Current selection: fragment source key or a project id */
  value: string;
  /** Callback when user selects a different option */
  onChange: (value: string) => void;
  /** List of video-editor projects for this fragment (only rendered options with completed render should be selectable if needed) */
  projects: webApi.EnrichedVideoEditorProjectListItemDto[];
  /** Whether the original fragment media option should be shown. */
  includeFragmentOption?: boolean;
}

/** Content for a single option row: icon, primary text, and secondary (chip and optional extra). */
interface OptionRowContent {
  icon: ReactNode;
  primary: ReactNode;
  secondary: ReactNode;
}

/** Renders the same option row layout used in both the dropdown and the select trigger. */
function renderOptionRow(content: OptionRowContent, compact: boolean): ReactNode {
  const inner = (
    <>
      <ListItemIcon sx={compact ? { minWidth: 36 } : undefined}>{content.icon}</ListItemIcon>
      <ListItemText
        primary={content.primary}
        secondary={content.secondary}
        sx={compact ? { my: 0 } : undefined}
      />
    </>
  );
  return compact ? (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{inner}</Box>
  ) : (
    inner
  );
}

/**
 * MUI Select to choose between base fragment media and video-edition projects for social export.
 * Shows fragment option with VideoFile icon; each project with timeline icon and updatedAt as subtitle.
 */
export function SocialExportMediaSelector({
  value,
  onChange,
  projects,
  includeFragmentOption = true,
}: SocialExportMediaSelectorProps) {
  const { $t } = useIntl();

  const handleChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  const fragmentContent: OptionRowContent = {
    icon: <VideoFileIcon fontSize="small" />,
    primary: $t({
      id: 'fragments.socialExport.mediaSelector.fragmentOption',
      defaultMessage: 'Media original',
    }),
    secondary: '',
  };

  const getProjectContent = (
    project: webApi.EnrichedVideoEditorProjectListItemDto,
  ): OptionRowContent => ({
    icon: <ViewTimelineOutlinedIcon fontSize="small" />,
    primary: (
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
          my: 0.5,
        }}
      >
        {project.name}
        <Chip
          size="small"
          variant="outlined"
          label={$t({
            id: 'fragments.socialExport.mediaSelector.edition',
            defaultMessage: 'Edición',
          })}
          sx={{ fontSize: '11px' }}
        />
      </Box>
    ),
    secondary:
      project.renderJob?.status === 'COMPLETED' && project.renderJob.finishedOn ? (
        <>
          {$t({
            id: 'fragments.videoEditions.finishedOn',
            defaultMessage: 'Generado',
          })}{' '}
          <LocaleRelativeTime value={project.renderJob.finishedOn} />
        </>
      ) : (
        <LocaleRelativeTime value={project.updatedAt} />
      ),
  });

  const label = $t({
    id: 'fragments.socialExport.mediaSelector.label',
    defaultMessage: 'Video a publicar',
  });

  const renderedProjects = projects.filter((project) => project.renderJob?.status === 'COMPLETED');

  return (
    <FormControl fullWidth sx={{ minWidth: 280 }}>
      <InputLabel id="social-export-media-selector-label">{label}</InputLabel>
      <Select
        labelId="social-export-media-selector-label"
        value={value}
        label={label}
        onChange={handleChange}
        renderValue={(v) => {
          if (v === SOCIAL_EXPORT_FRAGMENT_SOURCE) return renderOptionRow(fragmentContent, true);
          const p = renderedProjects.find((x) => x.id === v);
          if (!p) return v;
          return renderOptionRow(getProjectContent(p), true);
        }}
      >
        {includeFragmentOption && (
          <MenuItem value={SOCIAL_EXPORT_FRAGMENT_SOURCE} sx={{ minHeight: '56px !important' }}>
            {renderOptionRow(fragmentContent, false)}
          </MenuItem>
        )}
        {renderedProjects.map((project) => (
          <MenuItem key={project.id} value={project.id} sx={{ minHeight: 56 }}>
            {renderOptionRow(getProjectContent(project), false)}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}
